import PublicLayout from "@/components/layout/PublicLayout";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const STEPS = [
    {
        n: "01",
        t: "Choose your path",
        d: "Buying a home, or analyzing an investment property. We tailor your experience.",
    },
    {
        n: "02",
        t: "Upload your inspection PDF",
        d: "Drag, drop, done. We accept text-based inspection PDFs up to 25MB.",
    },
    {
        n: "03",
        t: "We parse, organize, structure",
        d: "Findings are extracted, categorized by system, and scored for severity and timeline.",
    },
    {
        n: "04",
        t: "Create a free account",
        d: "After processing starts, you'll create a free account so your report can be securely saved.",
    },
    {
        n: "05",
        t: "Review your preview",
        d: "See a calm overview: number of issues, categories detected, executive teaser, and highlights.",
    },
    {
        n: "06",
        t: "Unlock the full report",
        d: "Buy Inspection Summary ($49) or Closing Strategy ($99). Reports save to your dashboard.",
    },
    {
        n: "07",
        t: "Negotiate from clarity",
        d: "Use prioritized leverage, suggested asks, and specialist recommendations to negotiate intelligently.",
    },
];

export default function HowItWorks() {
    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-12">
                    <div className="bl-eyebrow" data-reveal="slide-left">How it works</div>
                    <h1
                        className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl"
                        data-reveal="slide-left-lg"
                        data-reveal-delay="1"
                    >
                        A calm, structured path from{" "}
                        <span className="font-italic-accent text-bl-primary">
                            inspection
                        </span>{" "}
                        to negotiation.
                    </h1>

                    <div
                        className="mt-10 overflow-hidden rounded-3xl border border-bl shadow-sm bg-bl-cream"
                        data-reveal="slide-right-lg"
                        data-reveal-delay="2"
                        data-testid="how-it-works-hero-image-wrap"
                    >
                        <picture>
                            <source srcSet="/how-it-works-hero-claymation.webp" type="image/webp" />
                            <img
                                src="/how-it-works-hero-claymation.png"
                                alt="Claymation four-stage process ribbon: Upload, Organize, Prioritize, Negotiate."
                                loading="eager"
                                fetchPriority="high"
                                className="w-full h-auto block"
                                data-testid="how-it-works-hero-image"
                            />
                        </picture>
                    </div>
                </div>
            </section>

            <section className="bl-container py-16">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4 min-w-0" data-reveal="slide-left">
                        <p className="text-bl-muted leading-relaxed">
                            Inspection reports are dense, jargon-heavy, and often
                            overwhelming. Buyer&apos;s Leverage organizes the findings the way
                            a calm advisor would — with severity, timeline, and
                            negotiation framing baked in.
                        </p>
                        <Link
                            to="/upload"
                            className="bl-btn-primary mt-7"
                            data-testid="how-upload-cta"
                        >
                            Upload Inspection <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <ol className="lg:col-span-8 min-w-0 space-y-4">
                        {STEPS.map((s, i) => (
                            <li
                                key={s.n}
                                className="bl-card p-6 md:p-7 flex gap-5"
                                data-testid={`how-step-${s.n}`}
                                data-reveal="rise-sm"
                                data-reveal-delay={Math.min(5, (i % 5) + 1)}
                            >
                                <div className="font-mono-bl text-bl-primary text-sm tracking-wider w-10 flex-shrink-0">
                                    {s.n}
                                </div>
                                <div>
                                    <h3 className="font-display font-semibold text-lg md:text-xl text-bl">
                                        {s.t}
                                    </h3>
                                    <p className="mt-2 text-bl-muted leading-relaxed">
                                        {s.d}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>
        </PublicLayout>
    );
}
